import { AxiosRequestConfig, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { UserAuthData } from "../../../screens/authentication/hooks/use-auth/types/UserAuthData";
import { httpClient } from "../httpClient";

type CreateAuthenticatedApiClientParameters = {
    userAuthData: UserAuthData | null;
    isUserAuthenticated: () => boolean;
    navigate: NavigateFunction;
};

export const headerValues = {
    appJson: { "Content-Type": "application/json" },
};

export function createAuthenticatedApiClient({
    userAuthData,
    isUserAuthenticated,
    navigate,
}: CreateAuthenticatedApiClientParameters) {
    const getJwtToken = (): string | null => {
        if (!isUserAuthenticated() || userAuthData?.jwtToken === null || userAuthData?.jwtToken === undefined) {
            navigate("/login");
            return null;
        }

        return userAuthData.jwtToken;
    };

    const getHeaders = (
        jwtToken: string,
        headers?: Record<string, string>,
    ): Record<string, string> => ({
        ...(headers ?? {}),
        Authorization: `Bearer ${jwtToken}`,
    });

    const get = <T>(
        url: string,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T> | null> => {
        const jwtToken = getJwtToken();

        if (jwtToken === null) {
            return Promise.resolve(null);
        }

        return httpClient.instance.get<T>(url, {
            ...config,
            headers: getHeaders(jwtToken, config?.headers as Record<string, string> | undefined),
        });
    };

    const post = <TResponse, TBody = unknown>(
        url: string,
        data?: TBody,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<TResponse> | null> => {
        const jwtToken = getJwtToken();

        if (jwtToken === null) {
            return Promise.resolve(null);
        }

        return httpClient.instance.post<TResponse>(url, data, {
            ...config,
            headers: getHeaders(jwtToken, config?.headers as Record<string, string> | undefined),
        });
    };

    const put = <TResponse, TBody = unknown>(
        url: string,
        data?: TBody,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<TResponse> | null> => {
        const jwtToken = getJwtToken();

        if (jwtToken === null) {
            return Promise.resolve(null);
        }

        return httpClient.instance.put<TResponse>(url, data, {
            ...config,
            headers: getHeaders(jwtToken, config?.headers as Record<string, string> | undefined),
        });
    };

    const remove = <T>(
        url: string,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T> | null> => {
        const jwtToken = getJwtToken();

        if (jwtToken === null) {
            return Promise.resolve(null);
        }

        return httpClient.instance.delete<T>(url, {
            ...config,
            headers: getHeaders(jwtToken, config?.headers as Record<string, string> | undefined),
        });
    };

    return {
        get,
        post,
        put,
        delete: remove,
    };
}
